import Auth from '@aws-amplify/auth';
import { AxiosInstance } from 'axios';
import config from 'config/Config';
import { NOTIFICATION_STATUS } from 'utils/constants';
import { initAxios } from 'utils/axios';

/**
 * Marks a notification read with a request that survives a full-page unload
 * (`keepalive: true`), so a cross-app `/v2` navigation can fire immediately
 * without the browser aborting the in-flight read.
 *
 * Deliberately bypasses the axios instance: `keepalive` is a fetch-only flag
 * (axios uses XHR, which has no equivalent), so the bearer token is attached
 * by hand to mirror initAxios's request interceptor. Keep the auth header and
 * body shape in sync with `NotificationAPI.updateNotification`.
 *
 * Resolves once the request has been *dispatched* — it intentionally does NOT
 * await the response. That is what lets the caller navigate instantly while the
 * read still completes in the background after the document unloads.
 */
export const markNotificationReadKeepalive = async (
  id: string
): Promise<void> => {
  const user = await Auth.currentAuthenticatedUser();
  const token = user.signInUserSession.idToken.jwtToken;
  const baseURL = config.get('backendURL');
  // Fire-and-forget: not awaited so the caller can navigate the instant the
  // request is dispatched. Swallow rejection so a network failure (the request
  // we can't observe across the unload) never surfaces as an unhandled
  // rejection.
  fetch(`${baseURL}notifications/${id}/`, {
    method: 'PUT',
    keepalive: true,
    headers: {
      Authorization: `bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status: NOTIFICATION_STATUS.READ }),
  }).catch(() => undefined);
};

class NotificationAPI {
  instance: AxiosInstance;
  constructor() {
    this.instance = initAxios(config.get('backendURL'));
  }

  fetchNotifications = async () => {
    const { data } = await this.instance.get(`notifications/`);
    return data;
  };

  updateNotification = async (id: string, updateData: any) => {
    const { data } = await this.instance.put(
      `notifications/${id}/`,
      updateData
    );
    return data;
  };

  markAllAsRead = async () => {
    const { data } = await this.instance.put('notifications/mark-read');
    return data;
  };

  markAllAsSeen = async () => {
    const { data } = await this.instance.put('notifications/mark-seen');
    return data;
  };
}

const notificationAPI = new NotificationAPI();

export default notificationAPI;
